import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Trash2, MessageSquareHeart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth.jsx';

// Storage keys
// IMPORTANT — chat history is namespaced PER USER (user_id) so logging in as
// a different account never reveals the previous user's messages. The
// auth.jsx logout() / login() flows also wipe these on transitions; this
// namespacing is defense-in-depth in case those callbacks don't fire (e.g.
// browser crash, JWT expiry timeout, multi-tab edge cases).
const STORAGE_KEY_PREFIX = 'qf-support-chat-history:';
const STORAGE_KEY_PUBLIC = 'qf-support-chat-history:public';  // pre-login chats
const NUDGE_DISMISSED_KEY = 'qf-support-chat-nudge-dismissed';

const QUICK_REPLIES = [
    'How does pricing work?',
    'How does the 7-day free trial work?',
    'How do I run an AI roof estimate?',
    'Where do I cancel my plan?',
];

function _storageKeyFor(user) {
    return user?.id ? `${STORAGE_KEY_PREFIX}${user.id}` : STORAGE_KEY_PUBLIC;
}

function loadHistory(user) {
    try {
        const raw = sessionStorage.getItem(_storageKeyFor(user));
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}
function saveHistory(user, h) {
    try { sessionStorage.setItem(_storageKeyFor(user), JSON.stringify(h)); } catch { /* ignore */ }
}

// Floating bottom-right "Need help?" pill that expands into a small chat panel.
// Available everywhere — public pages and inside /app/*. Uses GPT-4o-mini on
// the backend. No "talk to a human" escalation: support is async via the
// public support email shown in the footer of legal pages.
export function SupportChat() {
    const { user } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [showNudge, setShowNudge] = useState(false);
    // Start with an empty history; the effect below loads the right per-user
    // history once we know who's signed in (user identity can change after
    // initial render, e.g. on login/logout transitions).
    const [history, setHistory] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const scrollerRef = useRef(null);
    const inputRef = useRef(null);

    // Hide on auth screens (login/signup) and on the public quote viewer — chat
    // there is more distracting than helpful.
    const path = location.pathname || '';
    const hidden = path === '/login' || path === '/signup' || path.startsWith('/q/');

    // Re-load history whenever the signed-in user identity changes. We use
    // the React-recommended "compare-and-update-during-render" pattern instead
    // of a useEffect — both because it's the canonical way to "reset state on
    // prop change" since React 18.3, and because it satisfies the
    // react-hooks/set-state-in-effect rule.
    const [currentUserId, setCurrentUserId] = useState(user?.id || null);
    if (currentUserId !== (user?.id || null)) {
        setCurrentUserId(user?.id || null);
        setHistory(loadHistory(user));
    }

    // (Previously: a "Stuck? Ask me anything →" nudge auto-popped 2s after
    // mount. Removed June 2026 — the always-visible bottom-right pill is
    // discoverable enough on its own.)

    useEffect(() => {
        saveHistory(user, history);
        // Scroll to bottom when new message arrives
        if (scrollerRef.current) {
            scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
    }, [history, user]);

    // Auto-focus the input when the panel opens.
    useEffect(() => {
        if (open && inputRef.current) {
            // Small delay so the panel transition finishes before focus
            const t = setTimeout(() => inputRef.current?.focus(), 120);
            return () => clearTimeout(t);
        }
    }, [open]);

    const dismissNudge = () => {
        setShowNudge(false);
        localStorage.setItem(NUDGE_DISMISSED_KEY, '1');
    };

    const openChat = () => {
        setOpen(true);
        dismissNudge();
        if (history.length === 0) {
            setHistory([{
                role: 'assistant',
                content: `Hey${user?.full_name ? ` ${user.full_name.split(' ')[0]}` : ''}! I'm the QuotesFlow assistant. Ask me anything about pricing, the AI estimator, or how to get the most out of your account.`,
            }]);
        }
    };

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        if (!user) {
            // Public-page visitor — show a soft fallback (and route them to signup)
            setHistory((h) => [
                ...h,
                { role: 'user', content: msg },
                { role: 'assistant', content: "I can answer all your questions once you sign in. Want a quick walkthrough? Tap **Start 7-day free trial** at the top to create an account and start your trial (card required, no charge for 7 days)." },
            ]);
            setInput('');
            return;
        }
        const newHistory = [...history, { role: 'user', content: msg }];
        setHistory(newHistory);
        setInput('');
        setSending(true);
        try {
            const r = await api.post('/support/chat', {
                message: msg,
                history: newHistory.slice(-10),
            });
            setHistory((h) => [...h, { role: 'assistant', content: r.data?.reply || '(no reply)' }]);
        } catch (e) {
            setHistory((h) => [
                ...h,
                {
                    role: 'assistant',
                    content:
                        "I'm having trouble reaching the assistant right now. Please try again in a moment — or email **support@quotesflowairoofing.com** and a human will get back to you.",
                },
            ]);
        } finally {
            setSending(false);
        }
    };

    // Wipe history both from React state and from the correct per-user
    // sessionStorage key. (Previously this referenced an undefined `STORAGE_KEY`
    // which silently no-op'd — so old messages persisted across "Clear" clicks.)
    const clear = () => {
        setHistory([]);
        try { sessionStorage.removeItem(_storageKeyFor(user)); } catch { /* noop */ }
    };

    if (hidden) return null;

    return (
        <>
            {/* Floating launcher */}
            {!open && (
                <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2" data-testid="support-chat-launcher-container">
                    {showNudge && (
                        <div
                            className="relative max-w-[230px] bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2 text-xs text-slate-700 animate-in fade-in slide-in-from-bottom-3"
                            data-testid="support-chat-nudge"
                        >
                            <button
                                onClick={dismissNudge}
                                aria-label="Dismiss"
                                className="absolute -top-1.5 -right-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-full p-0.5"
                            >
                                <X className="h-3 w-3" />
                            </button>
                            Stuck on something? Ask me anything →
                        </div>
                    )}
                    <button
                        onClick={openChat}
                        className="group bg-slate-900 hover:bg-slate-800 text-white rounded-full shadow-xl pl-2 pr-5 py-2 flex items-center gap-2 transition-transform hover:-translate-y-0.5 ring-1 ring-slate-900/10"
                        data-testid="support-chat-launcher"
                        title="Open support chat"
                    >
                        <span className="h-8 w-8 rounded-full bg-amber-500 flex items-center justify-center text-slate-950 shadow-inner">
                            <MessageCircle className="h-4 w-4" strokeWidth={2.5} />
                        </span>
                        <span className="text-sm font-medium">Need help?</span>
                    </button>
                </div>
            )}

            {/* Expanded panel */}
            {open && (
                <div
                    className="fixed bottom-4 right-4 z-50 w-[92vw] max-w-[400px] h-[78vh] sm:h-[560px] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-3"
                    data-testid="support-chat-panel"
                >
                    {/* Header */}
                    <div className="relative px-4 py-3 bg-gradient-to-br from-slate-900 to-slate-800 text-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="relative h-9 w-9 rounded-lg overflow-hidden shadow-md ring-2 ring-amber-500/40 flex items-center justify-center bg-amber-500">
                                <img
                                    src="/branding/qf-logo.jpg"
                                    alt="QuotesFlow"
                                    className="h-full w-full object-cover"
                                    draggable={false}
                                />
                                <span
                                    className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-slate-900"
                                    aria-label="Assistant online"
                                />
                            </div>
                            <div className="leading-tight">
                                <div className="text-sm font-display font-semibold">QuotesFlow Assistant</div>
                                <div className="text-[10px] text-slate-300">Replies in seconds · here to help</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            aria-label="Close chat"
                            data-testid="support-chat-close"
                            className="hover:bg-white/10 rounded-md p-1.5 transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div
                        ref={scrollerRef}
                        className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50"
                        data-testid="support-chat-messages"
                    >
                        {history.map((m, i) => (
                            <ChatBubble key={i} role={m.role} content={m.content} />
                        ))}
                        {sending && (
                            <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="support-chat-thinking">
                                <span className="inline-flex gap-1">
                                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.3s]" />
                                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.15s]" />
                                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" />
                                </span>
                                <span>Thinking&hellip;</span>
                            </div>
                        )}
                    </div>

                    {/* Quick replies (only when there's just the welcome message) */}
                    {history.length <= 1 && (
                        <div className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5 bg-white border-t border-slate-100">
                            {QUICK_REPLIES.map((q) => (
                                <button
                                    key={q}
                                    onClick={() => send(q)}
                                    className="text-xs bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200 rounded-full px-3 py-1.5 transition-colors"
                                    data-testid={`support-chat-quickreply-${q.slice(0, 8)}`}
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <form
                        className="flex items-center gap-2 p-3 border-t border-slate-200 bg-white"
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                    >
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type your question…"
                            className="flex-1 text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 disabled:bg-slate-50 disabled:text-slate-400"
                            data-testid="support-chat-input"
                            disabled={sending}
                            maxLength={2000}
                            aria-label="Type your question"
                        />
                        <Button
                            type="submit"
                            size="icon"
                            disabled={sending || !input.trim()}
                            className="bg-amber-500 hover:bg-amber-400 text-slate-950 disabled:bg-slate-200 disabled:text-slate-500"
                            data-testid="support-chat-send"
                            aria-label="Send message"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                    </form>

                    {/* Footer mini-actions */}
                    <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-1 text-[10px] text-slate-500 bg-white">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={clear}
                                className="inline-flex items-center gap-1 hover:text-slate-800 underline-offset-2 hover:underline transition-colors"
                                data-testid="support-chat-clear"
                            >
                                <Trash2 className="h-3 w-3" /> Clear
                            </button>
                            <button
                                onClick={() => {
                                    // Pop the feedback widget. Loose coupling via window event.
                                    setOpen(false);
                                    setTimeout(() => window.dispatchEvent(new CustomEvent('qf:open-feedback')), 250);
                                }}
                                className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline transition-colors"
                                data-testid="support-chat-leave-feedback"
                            >
                                <MessageSquareHeart className="h-3 w-3" /> Leave feedback
                            </button>
                        </div>
                        <span className="text-slate-400">
                            <kbd className="bg-slate-100 border border-slate-200 rounded px-1 py-0.5 text-[9px] font-mono">Enter</kbd> to send
                        </span>
                    </div>
                </div>
            )}
        </>
    );
}

function ChatBubble({ role, content }) {
    const isUser = role === 'user';
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`chat-bubble-${role}`}>
            <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                    isUser
                        ? 'bg-slate-900 text-white rounded-br-sm'
                        : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'
                }`}
            >
                {renderMarkdownLite(content)}
            </div>
        </div>
    );
}

// Tiny markdown helper — bold (**...**) and inline links — enough for the
// assistant's typical formatting without pulling a full markdown library.
function renderMarkdownLite(text) {
    if (!text) return null;
    const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**')) {
            return <b key={i}>{p.slice(2, -2)}</b>;
        }
        return <span key={i}>{p}</span>;
    });
}
